<template>
  <div class="share-page" v-loading="loading" element-loading-text="分享图生成中...">
    <div class="share-poster-wrap">
      <img v-if="posterImg" :src="posterImg" class="share-poster-img" />
    </div>
    <div class="share-actions">
      <el-button type="primary" round icon="el-icon-download" :disabled="!posterImg" @click="savePoster">
        下载分享图片
      </el-button>
      <div class="share-tip" v-if="isWechat">微信内长按上方图片保存，或点击右上角"…"在浏览器中打开下载</div>
      <div class="share-tip" v-else>保存图片后可发送给好友或朋友圈</div>
      <div class="share-back" @click="goDetail">查看活动详情 &gt;</div>
    </div>

    <!-- 隐藏的海报生成容器：内容全部取自活动详情，底部二维码为详情页地址 -->
    <div class="poster-maker" ref="posterMaker">
      <div class="poster-bg">
        <div class="poster-card">
          <div class="poster-title">{{ infoData.title }}1</div>
          <div class="poster-divider">
            <i class="dot dot-l"></i>
            <i class="line"></i>
            <i class="dot dot-r"></i>
          </div>
          <div class="poster-subtitle">{{ infoData.category_name }}</div>
          <div class="poster-cover">
            <img :src="posterCover" />
          </div>
          <div class="poster-footer">
            <div class="poster-info">
              <div class="poster-info-item">
                <span class="label">活动开始时间：</span>
                <span class="value">{{ posterTime(infoData.start_time) }}</span>
              </div>
              <div class="poster-info-item">
                <span class="label">活动结束时间：</span>
                <span class="value">{{ posterTime(infoData.end_time) }}</span>
              </div>
              <div class="poster-info-item">
                <span class="label">活动地点：</span>
                <span class="value">{{ infoData.address }}</span>
              </div>
            </div>
            <div class="poster-qrcode">
              <img :src="activityQrCode" />
              <span class="poster-qrcode-tip">扫码查看详情</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import dayjs from "dayjs"
import { getActivityInfo } from "@/api/activity"
import QRCode from "qrcode"
import html2canvas from "html2canvas"
const request_base_path = require("@/utils/base_url")

export default {
  name: "ActivitySharePoster",
  data() {
    return {
      queryObj: {},
      infoData: {},
      loading: false,
      defaultImg: require("@/assets/default/image02.jpg"),
      activityQrCode: "",
      posterImg: "",
      isWechat: /micromessenger/i.test(navigator.userAgent),
    }
  },
  computed: {
    // 海报封面：转成同源地址，避免 html2canvas 因跨域无法读取像素
    posterCover() {
      return this.toSameOrigin(this.infoData.cover) || this.defaultImg
    }
  },
  created() {
    this.queryObj = this.$route.query || {}
  },
  mounted() {
    this.getInfo().then(() => {
      if (this.infoData.title) {
        this.generatePoster()
      }
    })
  },
  methods: {
    // 开发环境下把远程图片地址转为同源相对地址，走 devServer 代理，
    // 这样 html2canvas 才能读取像素；生产环境前后端同域，直接返回原地址
    toSameOrigin(url) {
      if (!url || process.env.NODE_ENV !== "development") return url
      const remote = new URL(request_base_path.dev_url).origin
      if (!url.startsWith(remote)) return url
      return url.slice(remote.length)
    },
    // 海报时间格式：2026.09.01 08:00
    posterTime(value) {
      if (!value) return ""
      return dayjs.unix(value).format("YYYY.MM.DD HH:mm")
    },
    // 获取详情信息
    getInfo() {
      this.loading = true
      const params = { id: this.queryObj?.id ?? "" }
      return getActivityInfo(params).then((res) => {
        this.loading = false
        if (res.code === 0) {
          this.infoData = res.data || {}
          // 海报内二维码：扫码查看活动详情
          const detailUrl = `${request_base_path.pro_url}#/home/activity/detail?id=${this.queryObj.id}`
          QRCode.toDataURL(detailUrl).then((url) => {
            this.activityQrCode = url
          })
        }
      }).catch(() => {
        this.loading = false
      })
    },
    // 生成分享海报
    async generatePoster() {
      this.loading = true
      try {
        await this.loadPosterImage()
        const el = this.$refs.posterMaker
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: null,
          logging: false,
          width: el.offsetWidth,
          height: el.offsetHeight,
        })
        this.posterImg = canvas.toDataURL("image/png")
      } catch (error) {
        console.error("海报生成失败", error)
        this.$message.error("海报生成失败，请刷新重试")
      } finally {
        this.loading = false
      }
    },
    // 预加载海报封面图，避免 html2canvas 读取时图片未完成加载导致空白
    loadPosterImage() {
      return new Promise((resolve) => {
        // 使用与海报 DOM 相同的同源地址，保证缓存命中且不触发跨域
        const cover = this.posterCover
        const img = new Image()
        let resolved = false
        const done = () => {
          if (resolved) return
          resolved = true
          resolve()
        }
        img.onload = done
        img.onerror = done
        img.src = cover
        setTimeout(done, 1200)
      })
    },
    // 下载分享图片
    savePoster() {
      if (!this.posterImg) return
      const link = document.createElement("a")
      link.href = this.posterImg
      link.download = `${this.infoData.title || "活动分享"}.png`
      link.click()
    },
    // 跳转活动详情页
    goDetail() {
      this.$router.push({ path: "/home/activity/detail", query: { id: this.queryObj.id } })
    },
  },
}
</script>
<style scoped lang="scss">
.share-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #eaf2ff 0%, #f7f9fc 100%);
  padding: 24px 16px 40px;
  box-sizing: border-box;

  .share-poster-wrap {
    display: flex;
    justify-content: center;

    .share-poster-img {
      width: 320px;
      max-width: 100%;
      border-radius: 8px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }
  }

  .share-actions {
    margin-top: 24px;
    text-align: center;

    .share-tip {
      margin-top: 12px;
      font-size: 13px;
      color: #999999;
      line-height: 1.6;
    }

    .share-back {
      margin-top: 16px;
      font-size: 14px;
      color: #005cbb;
      cursor: pointer;
    }
  }
}

// 海报生成器：移出可视区域，但仍可被 html2canvas 捕获
.poster-maker {
  position: fixed;
  top: 0;
  left: -9999px;
  z-index: -1;
  width: 320px;
  font-family: "Microsoft YaHei", "PingFang SC", sans-serif;

  .poster-bg {
    position: relative;
    width: 320px;
    padding: 50px 16px 40px;
    background: url('~@/assets/bg.png') no-repeat;
    background-size: 100% 100%;
    // background: linear-gradient(180deg, #6aaaff 0%, #3b86ff 100%);
    // background-image:
    //   linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
    //   linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px),
    //   linear-gradient(180deg, #6aaaff 0%, #3b86ff 100%);
    // background-size: 24px 24px, 24px 24px, 100% 100%;
    box-sizing: border-box;
  }

  .poster-card {
    position: relative;
    background: #ffffff;
    border-radius: 16px;
    padding: 30px 16px 20px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    text-align: center;
  }

  .poster-title {
    font-size: 22px;
    font-weight: bold;
    color: #333333;
    line-height: 1.4;
    margin-bottom: 16px;
  }

  // 标题与副标题之间的虚线分隔，两端带半圆凹点
  .poster-divider {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    .line {
      flex: 1;
      height: 0;
      border-top: 2px dashed #d0d7e2;
    }

    .dot {
      position: absolute;
      top: 50%;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #62A1FD;
      transform: translateY(-50%);
      // opacity: 0.7;

      &.dot-l {
        left: -25px;
      }

      &.dot-r {
        right: -25px;
      }
    }
  }

  .poster-subtitle {
    font-size: 16px;
    color: #333333;
    line-height: 1.4;
    margin-bottom: 16px;
  }

  .poster-cover {
    width: 100%;
    height: 170px;
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 18px;
    background-color: #f5f5f5;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .poster-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;

    .poster-info {
      flex: 1;
      min-width: 0;
      padding-right: 6px;
      text-align: left;

      .poster-info-item {
        font-size: 11px;
        line-height: 1.9;
        color: #333333;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .label {
          color: #666666;
        }

        .value {
          color: #333333;
        }
      }
    }

    .poster-qrcode {
      width: 78px;
      text-align: center;
      flex-shrink: 0;

      img {
        width: 70px;
        height: 70px;
        display: block;
        margin: 0 auto;
      }

      .poster-qrcode-tip {
        font-size: 10px;
        color: #999999;
        margin-top: 4px;
      }
    }
  }
}
</style>
